import {
  Flex,
  IconChartLineUp,
  IconWarning,
  IconXOctagon,
  Menu,
  MenuButton,
  Spinner,
  Stack,
  Text,
  csx,
  useMenuState,
} from '@vtex/admin-ui'
import { defineMessages, useIntl } from 'react-intl'
import { useAnalytics } from 'use-analytics'

import { usePromotions } from 'hooks'
import { useEffect } from 'react'
import { Event } from 'typings'

const messages = defineMessages({
  buttonLabel: { id: 'admin/promotions.list.usage' },
  active: { id: 'admin/promotions.list.usage.active' },
  counter: { id: 'admin/promotions.list.usage.counter' },
  quota: { id: 'admin/promotions.list.usage.quota' },
})

type ProgressBarProps = {
  progress: number
}

function ProgressBar(props: ProgressBarProps) {
  const { progress } = props

  let gradient = ['#368369', '#CEE8DE']

  if (progress > 0.9) {
    gradient = ['#CB4242', '#FEE1E1']
  } else if (progress > 0.8) {
    gradient = ['#FFBA52', '#F6E0BA']
  }

  return (
    <div
      className={csx({
        width: 'full',
        height: 10,
        background: '$gray10',
        borderRadius: 4,
        marginTop: '$space-5',
      })}
    >
      <div
        className={csx({
          height: 'full',
          background: `linear-gradient(to right, ${gradient[0]}, ${gradient[1]})`,
          width: `${progress * 100}%`,
          borderRadius: 4,
        })}
      />
    </div>
  )
}

function UsageInfoContent() {
  const { formatMessage, formatNumber } = useIntl()
  const { track } = useAnalytics()
  const { data } = usePromotions()

  useEffect(() => {
    data?.meta &&
      track(Event.USAGE_INFO_VIEWED, {
        limit: data.meta.limit,
        usage: data.meta.usage,
      })
  }, [data?.meta, track])

  if (!data?.meta) {
    return (
      <Flex className={csx({ height: 90 })} align="center" justify="center">
        <Spinner size={30} />
      </Flex>
    )
  }

  const { usage, limit } = data.meta

  const usedQuota = usage / limit

  return (
    <>
      <Flex direction="row" justify="space-between">
        <Flex direction="column" align="flex-start">
          <Text tone="secondary">{formatMessage(messages.active)}</Text>
          <Text
            variant="display"
            className={csx({ marginTop: '$space-2', marginRight: '$space-8' })}
          >
            {formatMessage(messages.counter, {
              used: usage,
              total: limit,
            })}
          </Text>
        </Flex>

        <Flex direction="column" align="flex-end">
          <Text tone="secondary">{formatMessage(messages.quota)}</Text>
          <Flex className={csx({ marginTop: '$space-2' })} align="center">
            <Text variant="display">{formatNumber(limit - usage)}</Text>
            <div
              className={csx({ ':not(:empty)': { marginLeft: '$space-3' } })}
            >
              {usedQuota > 0.9 ? (
                <IconXOctagon
                  weight="fill"
                  className={csx({ color: '$critical', height: 24, width: 24 })}
                />
              ) : usedQuota > 0.8 ? (
                <IconWarning
                  weight="fill"
                  className={csx({ color: '$warning', height: 24, width: 24 })}
                />
              ) : null}
            </div>
          </Flex>
        </Flex>
      </Flex>

      <ProgressBar progress={usedQuota} />
    </>
  )
}

function UsageInfo() {
  const { formatMessage } = useIntl()
  const state = useMenuState()

  return (
    <div
      className={csx({
        '>button >div >svg': { display: 'none' },
      })}
    >
      <MenuButton
        state={state}
        variant="neutralTertiary"
        label={
          <Stack direction="row" space="$space-2">
            <IconChartLineUp />
            <Text tone="secondary" variant="action1">
              {formatMessage(messages.buttonLabel)}
            </Text>
          </Stack>
        }
      />
      <Menu state={state}>
        <div
          className={csx({ minWidth: 400, minHeight: 86, margin: '$space-5' })}
        >
          {state.open && <UsageInfoContent />}
        </div>
      </Menu>
    </div>
  )
}

export { UsageInfo }
